/**
 * Admin ban-appeals API. Backed by moderation-service:
 *
 *   GET  /admin/ban-appeals?status=pending|accepted|denied|open
 *   POST /admin/ban-appeals/:id/decide  { decision, note }
 *
 * Senior-moderator+ only on the decide path (enforced server-side).
 * Voice rule on UI text: matter-of-fact, no security-theater.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';

export interface BanAppealRow {
  id: string;
  userId: string;
  status: string;
  statement: string;
  idEvidenceS3Key: string | null;
  slaDeadline: string;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export type AppealStatusFilter = 'pending' | 'accepted' | 'denied' | 'open';

export const banAppealKeys = {
  all: ['admin-ban-appeals'] as const,
  list: (status?: AppealStatusFilter) => [...banAppealKeys.all, 'list', status ?? 'open'] as const,
} as const;

export function useBanAppealsQuery(status?: AppealStatusFilter) {
  return useQuery({
    queryKey: banAppealKeys.list(status),
    queryFn: () => {
      const qs = status ? `?status=${status}` : '';
      return apiFetch<{ appeals: BanAppealRow[] }>({
        method: 'GET',
        url: `/admin/ban-appeals${qs}`,
      });
    },
  });
}

export function useDecideBanAppealMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; decision: 'accept' | 'deny'; note: string }) =>
      apiFetch<{ status: string }>({
        method: 'POST',
        url: `/admin/ban-appeals/${encodeURIComponent(args.id)}/decide`,
        body: { decision: args.decision, note: args.note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: banAppealKeys.all });
    },
  });
}
